// Pure read-only summarization of a submission's answer for the vendor review
// screen. Returns display "parts" so a single question can carry an answer value
// plus uploaded files and/or adopted policies. Kept React-free so it can be
// unit-tested directly (the web app has no DOM/component test runner).

import type { Question, Submission } from './types.ts';

export type AnswerPart =
  | { kind: 'text'; value: string }
  | { kind: 'list'; values: string[] }
  | { kind: 'files'; names: string[] }
  | { kind: 'adoptions'; items: { mode: 'adopted' | 'uploaded'; option: string | null }[] }
  | { kind: 'signature'; signerName: string; signedAt: string; dataUrl: string };

type SignatureAnswerValue = {
  signature_data_url: string;
  signer_name: string;
  signed_at: string;
};

function isSignatureAnswer(value: unknown): value is SignatureAnswerValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.signature_data_url === 'string' &&
    typeof v.signer_name === 'string' &&
    typeof v.signed_at === 'string'
  );
}

/**
 * Summarize a single question's answer into read-only display parts. An empty
 * array means "nothing recorded" (the caller renders "Not answered").
 * Informational and image questions read as "Acknowledged".
 */
export function summarizeAnswer(
  question: Question,
  submission: Submission | undefined,
): AnswerPart[] {
  if (question.type === 'image_info') return [{ kind: 'text', value: 'Acknowledged' }];
  if (question.type === 'informational') {
    const answer = submission?.answer;
    const watched =
      !!answer &&
      typeof answer === 'object' &&
      typeof (answer as { watched_at?: unknown }).watched_at === 'string';
    return watched || !question.video_url ? [{ kind: 'text', value: 'Acknowledged' }] : [];
  }

  const parts: AnswerPart[] = [];
  const answer = submission?.answer ?? null;

  if (isSignatureAnswer(answer)) {
    parts.push({
      kind: 'signature',
      signerName: answer.signer_name,
      signedAt: answer.signed_at,
      dataUrl: answer.signature_data_url,
    });
  } else if (question.type === 'yesno') {
    if (answer === true) parts.push({ kind: 'text', value: 'Yes' });
    else if (answer === false) parts.push({ kind: 'text', value: 'No' });
  } else if (question.type === 'select') {
    if (typeof answer === 'string' && answer.trim()) parts.push({ kind: 'text', value: answer.trim() });
  } else if (question.type === 'multiselect' || question.type === 'multiselect_uploads') {
    if (Array.isArray(answer) && answer.length > 0) {
      parts.push({ kind: 'list', values: answer.map((v) => String(v)) });
    }
  } else if (question.type === 'text') {
    if (typeof answer === 'string' && answer.trim()) {
      parts.push({ kind: 'text', value: answer.trim() });
    }
  }

  const files = submission?.submission_files ?? [];
  if (files.length > 0) parts.push({ kind: 'files', names: files.map((f) => f.file_name) });

  const adoptions = submission?.submission_adoptions ?? [];
  if (adoptions.length > 0) {
    parts.push({
      kind: 'adoptions',
      items: adoptions.map((a) => ({ mode: a.mode, option: a.option_key })),
    });
  }

  return parts;
}
